import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Money } from "@shop-ledger/shared-types";
import { useAuth } from "../../auth/AuthContext";
import { getDashboardSummary } from "../../api/dashboardApi";
import type { SalesByDayOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { OnboardingChecklist } from "../../components/OnboardingChecklist";
import { OnlineStoreToggle } from "../../components/OnlineStoreToggle";
import { PeriodSummaryCards } from "../../components/PeriodSummaryCards";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const CHART_BLUE = "#1a73e8";
const CHART_GRID = "#e2e8f0";
const CHART_AXIS = "#64748b";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — there is no tenant-settings
// endpoint this app can read it from yet, same documented stand-in `TillPage.tsx` uses.

type Bucket = "day" | "week" | "month" | "year";

type Period = "this_month" | "this_year" | "last_12_months" | "all_time";

const PERIOD_OPTIONS: { key: Period; label: string }[] = [
  { key: "this_month", label: "This month" },
  { key: "this_year", label: "This year" },
  { key: "last_12_months", label: "Last 12 months" },
  { key: "all_time", label: "All time" },
];

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Turns a `Period` preset into the `date_from`/`date_to` pair `GET /reports/dashboard-
 * summary` accepts. Presets rather than raw date inputs — this page's whole point (see
 * the component doc comment) is one screen a non-accountant can read at a glance, and a
 * bare "From/To" pair is the accountant-facing `ProfitAndLossPage`'s pattern, not this
 * one's. `all_time` has no real "beginning" to send, so it uses a date safely before any
 * tenant could have been created instead — leaving `date_from` unset would just re-trigger
 * the backend's own trailing-365-day default (`reports.py`'s `dashboard_summary`), which
 * is exactly the "only this year" behavior this selector exists to get out from under. */
function periodToRange(period: Period): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (period) {
    case "this_month":
      return { dateFrom: isoDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))) };
    case "this_year":
      return { dateFrom: isoDate(new Date(Date.UTC(now.getUTCFullYear(), 0, 1))) };
    case "last_12_months":
      return { dateFrom: isoDate(new Date(now.getTime() - 365 * 86400000)) };
    case "all_time":
      return { dateFrom: "2000-01-01" };
  }
}

interface ChartPoint {
  key: string;
  label: string;
  revenue: number;
}

function isoWeekKey(date: Date): string {
  // Monday-start ISO week bucket, e.g. "2026-W29" — good enough for a trend chart
  // (not used for any financial calculation, just x-axis grouping).
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function rollUp(days: SalesByDayOut[], bucket: Bucket): ChartPoint[] {
  const buckets = new Map<string, { label: string; revenue: number; order: string }>();
  for (const row of days) {
    const date = new Date(`${row.day}T00:00:00Z`);
    let key: string;
    let label: string;
    if (bucket === "day") {
      key = row.day;
      label = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } else if (bucket === "week") {
      key = isoWeekKey(date);
      label = key;
    } else if (bucket === "month") {
      key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
      label = date.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    } else {
      key = String(date.getUTCFullYear());
      label = key;
    }
    const existing = buckets.get(key);
    const revenue = Number(row.revenue_total);
    if (existing) {
      existing.revenue += revenue;
    } else {
      buckets.set(key, { label, revenue, order: key });
    }
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, v]) => ({ key, label: v.label, revenue: v.revenue }));
}

/** Business-owner walkthrough follow-up — this app's first-ever home/overview page.
 * Everyone lands here by default (`RootRedirect` in `App.tsx`); the goal is one screen
 * that answers "are we making money, what's in stock, what's selling" without requiring
 * any accounting knowledge. */
export function DashboardPage() {
  const { ensureFreshToken, user } = useAuth();
  const [branchId, setBranchId] = useState("");
  const [bucket, setBucket] = useState<Bucket>("day");
  const [period, setPeriod] = useState<Period>("last_12_months");
  const periodLabel = PERIOD_OPTIONS.find((option) => option.key === period)?.label.toLowerCase() ?? "";

  const state = useAsyncData(
    () =>
      ensureFreshToken().then((token) =>
        getDashboardSummary(token, { branchId: branchId || undefined, ...periodToRange(period) }),
      ),
    [branchId, period],
  );

  const summary = state.data;
  const chartData = useMemo(() => rollUp(summary?.sales_by_day ?? [], bucket), [summary, bucket]);
  const netIncome = summary ? Money.of(summary.net_income) : null;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Dashboard</h1>
          <p className="page__subtitle">
            {user ? `Welcome back, ${user.email.split("@")[0]}.` : null} How the business is doing right now.
          </p>
        </div>
        <OnlineStoreToggle compact />
      </div>

      <OnboardingChecklist />

      {state.error ? <StatusBanner kind="error">{state.error}</StatusBanner> : null}

      <div className="filter-bar">
        <BranchSelect id="dash-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
        <div className="sl-bucket-toggle" role="group" aria-label="Period">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={["sl-bucket-toggle__option", option.key === period ? "sl-bucket-toggle__option--active" : null]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setPeriod(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {state.loading || !summary ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="stat-row">
            <div className="stat-tile">
              <p className="stat-tile__label">Net profit / loss ({periodLabel})</p>
              <p className={`stat-tile__value ${netIncome?.isNegative() ? "stat-tile__value--negative" : "stat-tile__value--positive"}`}>
                {Money.format(summary.net_income, TENANT_CURRENCY_FALLBACK)}
              </p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Revenue</p>
              <p className="stat-tile__value">{Money.format(summary.revenue_total, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Expenses</p>
              <p className="stat-tile__value">{Money.format(summary.expense_total, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Stock value (retail)</p>
              <p className="stat-tile__value">{Money.format(summary.stock_value_retail, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
            <div className="stat-tile">
              <Link to="/stock?tab=low-stock" className="stat-tile__link">
                <p className="stat-tile__label">Low stock alerts</p>
                <p className={`stat-tile__value ${summary.low_stock_count > 0 ? "stat-tile__value--negative" : ""}`}>
                  {summary.low_stock_count.toLocaleString()}
                </p>
              </Link>
            </div>
            <div className="stat-tile">
              <Link to="/staff" className="stat-tile__link">
                <p className="stat-tile__label">Active staff</p>
                <p className="stat-tile__value">{summary.active_staff_count.toLocaleString()}</p>
              </Link>
            </div>
          </div>

          <div className="quick-actions">
            <Link to="/expenses" className="sl-button sl-button--secondary sl-button--md">
              Add expense
            </Link>
            <Link to="/stock" className="sl-button sl-button--secondary sl-button--md">
              Add product / top up stock
            </Link>
            <Link to="/staff" className="sl-button sl-button--secondary sl-button--md">
              Invite staff
            </Link>
            <Link to="/reports/pnl" className="sl-button sl-button--ghost sl-button--md">
              View full Profit &amp; Loss
            </Link>
          </div>

          <PeriodSummaryCards />

          <div className="panel">
            <div className="panel__header-row">
              <h2 className="panel__title">Sales trend</h2>
              <div className="sl-bucket-toggle" role="group" aria-label="Group sales by">
                {(["day", "week", "month", "year"] as Bucket[]).map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={["sl-bucket-toggle__option", b === bucket ? "sl-bucket-toggle__option--active" : null]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setBucket(b)}
                  >
                    {b.charAt(0).toUpperCase() + b.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length === 0 ? (
              <p className="muted">No sales recorded in this range yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={CHART_GRID} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={{ stroke: CHART_GRID }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip
                    formatter={(value: number) => [value.toLocaleString(undefined, { maximumFractionDigits: 2 }), "Revenue"]}
                    contentStyle={{ borderRadius: 8, borderColor: CHART_GRID, fontSize: "0.875rem" }}
                  />
                  <Line type="monotone" dataKey="revenue" stroke={CHART_BLUE} strokeWidth={2} dot={{ r: 3, fill: CHART_BLUE }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="panel">
            <h2 className="panel__title">Top sellers by revenue</h2>
            {summary.top_sellers_by_revenue.length === 0 ? (
              <p className="muted">No sales recorded in this range yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, summary.top_sellers_by_revenue.length * 40)}>
                <BarChart
                  data={summary.top_sellers_by_revenue.slice(0, 8).map((s) => ({ name: s.name, revenue: Number(s.revenue) }))}
                  layout="vertical"
                  margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
                >
                  <CartesianGrid stroke={CHART_GRID} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} width={140} />
                  <Tooltip
                    formatter={(value: number) => [value.toLocaleString(undefined, { maximumFractionDigits: 2 }), "Revenue"]}
                    contentStyle={{ borderRadius: 8, borderColor: CHART_GRID, fontSize: "0.875rem" }}
                  />
                  <Bar dataKey="revenue" fill={CHART_BLUE} radius={[0, 4, 4, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
